import { getListingDetailsTitle } from '../../utils/listingDetails';

/**
 * Is this viewer a signed-in provider? Only they get the fit confirmation:
 * an anonymous visitor has no profile to compare against, and a company or
 * admin reading a listing is not expressing provider interest.
 *
 * The auth user has two shapes. Right after login it is the decoded JWT
 * ({ id, role, providerProfileId }); after /auth/me resolves it carries the
 * profile document instead ({ id, role, providerProfile }). Both are read.
 */
export function isSignedInProvider(auth) {
  if (!auth?.token) return false;
  const user = auth.user;
  if (!user) return false;
  const role = String(user.role || '').toUpperCase();
  if (role === 'CRNA' || role === 'PROVIDER') return true;
  // Admins are provisioned a shadow profile and a company user can carry a
  // stray one; neither is expressing provider interest.
  if (role === 'ADMIN' || role === 'GROUP') return false;
  return !!(user.providerProfileId || user.providerProfile);
}

/** The raw listing behind a transformed card, or the object itself. */
export function rawListingOf(listing) {
  return listing?._original?.listing || listing?._original || listing?.listing || listing;
}

/**
 * The id the swipe itself will be sent with. Card-level ids win, then the raw
 * payload's, then a normalizedId (the /providers map addresses jobs by it).
 */
export function listingIdOf(listing) {
  if (!listing) return null;
  if (typeof listing === 'string') return listing;
  const raw = rawListingOf(listing) || {};
  const candidates = [listing._id, listing.id, raw._id, raw.id, raw.normalizedId, listing.normalizedId];
  const found = candidates.find((v) => v !== undefined && v !== null && String(v).trim());
  return found ? String(found) : null;
}

/** What the dialog shows while the report loads. */
export function listingSubjectOf(listing) {
  if (!listing || typeof listing === 'string') return null;
  const raw = rawListingOf(listing) || {};
  const facilityName =
    raw.facilityName ||
    raw.publicDisplayName ||
    raw.facility?.name ||
    listing.facilityName ||
    null;
  const location =
    raw.location ||
    listing.location ||
    ([raw.city, raw.state].filter(Boolean).join(', ') || null);
  return {
    title: getListingDetailsTitle(raw) || listing.title || null,
    facilityName: typeof facilityName === 'string' ? facilityName : null,
    location: typeof location === 'string' ? location : null,
  };
}
